import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthState, UserClaims } from '@okta/okta-auth-js';
import { AUTH_KEYS } from '@/lib/auth/config';
import { oktaAuth, oktaConfigMissing } from '@/lib/auth/okta';

export type OktaUser = UserClaims;

interface AuthContextValue {
  user: OktaUser | null;
  isAuthenticated: boolean;
  /** True until the initial session-restore + token check has run. */
  isLoading: boolean;
  /** Start the Okta sign-in redirect. `returnTo` is restored after callback. */
  login: (returnTo?: string) => void;
  /** Sign out locally + at the Okta authorization server. */
  logout: () => void;
  /** Complete the OAuth callback: parse tokens from the URL and store them. */
  completeLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OktaUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to Okta's auth state and start the SDK service.
  useEffect(() => {
    // If config is missing, stay in "not authenticated / not loading" state.
    if (oktaConfigMissing) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const handler = async (state: AuthState) => {
      if (cancelled) return;
      if (state.isAuthenticated) {
        try {
          const claims = await oktaAuth.getUser();
          if (!cancelled) setUser(claims);
        } catch {
          if (!cancelled) setUser(null);
        }
        if (!cancelled) setIsAuthenticated(true);
      } else {
        if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    oktaAuth.authStateManager.subscribe(handler);
    // start() loads the existing session (if any) and triggers an initial event.
    void oktaAuth.start();

    return () => {
      cancelled = true;
      oktaAuth.authStateManager.unsubscribe(handler);
      void oktaAuth.stop();
    };
  }, []);

  const login = useCallback((returnTo?: string) => {
    if (returnTo) sessionStorage.setItem(AUTH_KEYS.returnTo, returnTo);
    void oktaAuth.signInWithRedirect();
  }, []);

  const logout = useCallback(() => {
    void oktaAuth.signOut();
  }, []);

  const completeLogin = useCallback(async () => {
    // Parses code/state from the URL, exchanges for tokens, stores them.
    await oktaAuth.handleLoginRedirect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated, isLoading, login, logout, completeLogin }),
    [user, isAuthenticated, isLoading, login, logout, completeLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
